import type {
  // FixedSizeJson,
  AnnotationItemJson,
  CylinderJson,
  MarkupTessElemJson,
  PMIJson,
  PolygonJson,
  PolylineJson,
  TextTessJson,
  TrianglesJson,
  VertexesJson,
} from 'contracts/PMIJson';
import type {
  BufferGeometry,
  Color,
  CylinderGeometry,
  LineBasicMaterial,
  LineLoop,
  LineSegments,
  MeshBasicMaterial,
} from 'three';
import { Vector3, BoxHelper, Mesh, Line } from 'three';
import { Group } from 'three';

import { MultiMap } from '@frontend/datatypes';
import { wait } from '@frontend/utils';

//eslint-disable-next-line no-restricted-imports
import { getOrCreateThreeColor } from '../../colors';
//eslint-disable-next-line no-restricted-imports
import type { PointJson } from '../../contracts/basic';
//eslint-disable-next-line no-restricted-imports
import { disposeThreeJS } from '../../geom-utils';
// import {Font} from './font';
import { AnnotationItem } from './AnnotationItem';
import { AnnotationSet } from './AnnotationSet';
import { parseCylinder } from './cylinder';
import { parsePolygon } from './polygon';
import { parsePolylines } from './polyline';
import { parseTriangles } from './triangles';
import { parseVertexes } from './vertexes';

const maxUIBlockMs = 50;

const blackColor = getOrCreateThreeColor(0);

type CollectedParsedState = {
  lastColor: Color | null;
  lastFrameDraw: Vector3 | null;
  frames: [(Mesh | Line | Group)[], Vector3][];
  cylindersByColor: Map<Color, CylinderJson['cylinder'][]>;
  polygonsByColor: Map<Color, PolygonJson['polygon'][]>;
  polylinesByColor: Map<Color, PolylineJson['polyline'][]>;
  textsByColor: Map<Color, TextTessJson['text'][]>;
  trianglesByColor: Map<Color, TrianglesJson['triangles']>;
  vertexesByColor: Map<Color, VertexesJson['vertexes']>;
};

export class PMIData {
  // public materialProps: ReadonlyArray<Readonly<MaterialPropJson>>;
  // public parameters: Record<string, Record<string, string | number>>;

  public readonly geometry: Group = new Group();
  public readonly annotations: Map<AnnotationItemJson['id'], AnnotationItem> =
    new Map();
  public readonly annotationSets: Map<number, AnnotationSet> = new Map();
  public readonly annotationSetByName: Map<string, AnnotationSet> = new Map();
  private annotationsByType: Map<string, MultiMap<string, AnnotationItem>> =
    new Map(); //Annotations by type and subType

  private json: PMIJson | undefined;
  private _boundBox?: BoxHelper;
  private isDisposed?: boolean;

  constructor(json: PMIJson) {
    this.json = json;
    this.geometry.name = 'pmi';
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    if (this.json) {
      delete this.json;
    }
    disposeThreeJS(this.geometry, this._boundBox);
    this.annotations.clear();
    this.annotationSets.clear();
    this.annotationSetByName.clear();
    this.annotationsByType.clear();
  }

  public async hydrate(isVisible?: boolean) {
    if (this.json) {
      //TODO: When json.parameters and json.materialProps types are finalized, parse them
      // this.materialProps = Object.freeze(
      //   json.materialProps.map<MaterialPropJson>(Object.freeze),
      // );

      const collectedParsedState: CollectedParsedState = {
        lastColor: null,
        lastFrameDraw: null,
        frames: [],
        cylindersByColor: new Map(),
        polygonsByColor: new Map(),
        polylinesByColor: new Map(),
        textsByColor: new Map(),
        trianglesByColor: new Map(),
        vertexesByColor: new Map(),
      };

      for (const [
        annotationIndex,
        { data, id, Leader: leader, name, type, subType },
      ] of this.json.annotationItems?.entries() ?? []) {
        //parse annotationItem's data's markup tesselation elements
        if (data) {
          for (const [markupTessIndex, markupTessElem] of data.entries()) {
            await this.collectMarkupTessellation(
              annotationIndex,
              markupTessIndex,
              markupTessElem,
              collectedParsedState,
            );
          }
        }

        //If the last data item (markupTessElem) was for a frame, so just as we do for previous frameDraw modes:
        //parse the collected annotation data for each color in the frame and accumulate the frameGeometry as the items
        //for this final frame
        if (collectedParsedState.lastFrameDraw) {
          const frameGeometry = [];
          for await (const frameItem of this.parseCollectedAnnotationData(
            annotationIndex,
            collectedParsedState,
          )) {
            frameGeometry.push(frameItem);
          }
          collectedParsedState.frames.push([
            frameGeometry,
            collectedParsedState.lastFrameDraw.clone(),
          ]);
        }

        //parse leader data
        if (leader?.length) {
          for (const leaderData of leader) {
            if (leaderData?.data) {
              for (const [
                markupTessIndex,
                markupTessElem,
              ] of leaderData.data.entries()) {
                await this.collectMarkupTessellation(
                  annotationIndex,
                  `leader:${markupTessIndex}`,
                  markupTessElem,
                  collectedParsedState,
                );
              }
            }
          }
        }

        if (collectedParsedState.lastFrameDraw) {
          const frameGeometry = [];
          for await (const frameItem of this.parseCollectedAnnotationData(
            annotationIndex,
            collectedParsedState,
          )) {
            frameGeometry.push(frameItem);
          }
          collectedParsedState.frames.push([
            frameGeometry,
            collectedParsedState.lastFrameDraw.clone(),
          ]);
        }

        const parsed3DGeometry: (Mesh | Line | Group)[] = [];
        //Otherwise parse the collected annotation data for each color and accumulate the parsed 3D geometry
        for await (const item of this.parseCollectedAnnotationData(
          annotationIndex,
          collectedParsedState,
        )) {
          parsed3DGeometry.push(item);
        }

        //TODO: Collect frames (2D)
        // * I know the frame is parallel to the view port and one of its corners is located by the frame's vector
        // But I don't understand how to transform the 3D geometry in each frame.  Obviously the frame's geometry is
        // planar, so the camera looks straight at it.  It is kind of clear the frame's vector locates the corner that
        // must be aligned with the viewport.  But it is not clear how the frame's geometry should be scaled. For example
        // shouldn't there be a vector the the opposite corner? Or some other constraint to locate/size the frame?
        // I need to see a visual example of an implementation that uses a frame.

        //for (const frame of collectedParsedState.frames) {
        //  const [frameGeometry, frameVector] = frame;
        //  if (frameGeometry.length) {
        //    const {x, y, z} = frameVector;
        //    const frameGroup = new Group();
        //    frameGroup.add(...frameGeometry)
        //      .translateX(x)
        //      .translateY(y)
        //      .translateY(z)
        //      .rotateY(Math.PI);
        //    this.geometry.add(frameGroup);
        //  }
        //}

        //Index the annotation by type and subtype
        if (parsed3DGeometry.length) {
          const annotationGeometry =
            parsed3DGeometry.length > 1
              ? new Group().add(...parsed3DGeometry)
              : parsed3DGeometry[0];
          this.geometry.add(annotationGeometry);
          const annotationId = id ?? `annotationIndex:${annotationIndex}`;
          const annotation = new AnnotationItem(
            annotationId,
            annotationIndex,
            name,
            annotationGeometry,
            type,
            subType,
          );
          this.annotations.set(annotationId, annotation);
          let annotationsForType = this.annotationsByType.get(type);
          if (!annotationsForType) {
            annotationsForType = new MultiMap<string, AnnotationItem>();
            this.annotationsByType.set(type, annotationsForType);
          }
          annotationsForType.set(subType, annotation);
        }
      }

      this.geometry.visible = !!isVisible;
      this._boundBox = new BoxHelper(this.geometry);
      this._boundBox.geometry.computeBoundingBox();

      let last = performance.now();
      for (const [index, annotationSetJson] of (
        this.json.annotationSets || []
      ).entries()) {
        //hydrate AnnotationSet: Constructor adds itself to `this.annotationSets`
        if (annotationSetJson) {
          const annotationSet = new AnnotationSet(
            index,
            annotationSetJson,
            this.annotations,
            this.annotationSets,
          );
          this.annotationSetByName.set(annotationSet.name, annotationSet);
          const now = performance.now();
          if (now - last > maxUIBlockMs) {
            await wait();
            last = now;
          }
        } else {
          //logging for follow up in PMI parser
          console.error(
            `Unexpected annotationSet=${annotationSetJson} at index ${index} in PMI JSON`,
          );
        }
      }

      //TODO parse parameters. Waiting to iterate on json.parameters schema

      delete this.json;
    }
  }

  public get boundBox(): BoxHelper {
    return this._boundBox || new BoxHelper(this.geometry);
  }

  public get hasAnnotations() {
    return this.annotations.size > 0;
  }

  public get isVisible(): boolean {
    return this.geometry.visible;
  }

  public set isVisible(isVisible: boolean) {
    this.geometry.visible = isVisible;
  }

  public toggleVisible(): boolean {
    const isVisible = !this.geometry.visible;
    this.geometry.visible = isVisible;
    return isVisible;
  }

  public getAnnotationsBySetName(name: string): Iterable<AnnotationItem> {
    return this.annotationSetByName.get(name)?.annotations || [];
  }

  public getSubTypeAnnotationsForType(type: string): Iterable<string> {
    const annotationsForType = this.annotationsByType.get(type);
    return {
      *[Symbol.iterator]() {
        if (annotationsForType) {
          yield* annotationsForType.keys();
        }
      },
    };
  }

  public getSubTypeAnnotations(
    type: string,
    subType: string,
  ): Iterable<AnnotationItem> {
    const annotationsForType = this.annotationsByType.get(type);
    return {
      *[Symbol.iterator]() {
        if (annotationsForType) {
          const annotationsForSubType = annotationsForType.get(subType);
          if (annotationsForSubType) {
            yield* annotationsForSubType.values();
          }
        }
      },
    };
  }

  private logError(key: string, annotationIndex: number, err: unknown): void {
    //err is caught value. Typcally the thrown value will extend `Error` but js can `throw` any value.
    console.error(
      `Error parsing ${key} data from annotationItems[${annotationIndex}]`,
      err,
    );
  }

  //  private logNotImplemented(
  //    key: string,
  //    annotationIndex: number,
  //    markupTessIndex: string | number,
  //  ): void {
  //    console.warn(
  //      `Warning ${key} parsed from annotationItems[${annotationIndex}]data[${markupTessIndex}] is not implemented.`,
  //    );
  //  }

  private async *parseCylinders(
    cylindersByColor: Map<Color, CylinderJson['cylinder'][]>,
    annotationIndex: number,
  ): AsyncIterableIterator<Mesh<CylinderGeometry, MeshBasicMaterial>> {
    if (cylindersByColor.size) {
      let last = performance.now();
      for (const [color, cylinders] of cylindersByColor.entries()) {
        for (const cylinder of cylinders) {
          try {
            yield parseCylinder(cylinder, color);
          } catch (err) {
            this.logError('cylinder', annotationIndex, err);
          }
        }
        const now = performance.now();
        if (now - last > maxUIBlockMs) {
          await wait();
          last = now;
        }
      }
    }
  }

  private async *parsePolygons(
    polygonsByColor: Map<Color, PolygonJson['polygon'][]>,
    annotationIndex: number,
  ): AsyncIterableIterator<LineLoop> {
    if (polygonsByColor.size) {
      let last = performance.now();
      for (const [color, polygons] of polygonsByColor.entries()) {
        //TODO: consider merging polygon geometries before creating LineLoop?
        for (const polygon of polygons) {
          try {
            yield parsePolygon(polygon, color);
          } catch (err) {
            this.logError('polygon', annotationIndex, err);
          }
          const now = performance.now();
          if (now - last > maxUIBlockMs) {
            await wait();
            last = now;
          }
        }
      }
    }
  }

  private async *parsePolylines(
    polylinesByColor: Map<Color, PolylineJson['polyline'][]>,
    annotationIndex: number,
  ): AsyncIterableIterator<LineSegments<BufferGeometry, LineBasicMaterial>> {
    if (polylinesByColor.size) {
      let last = performance.now();
      for (const [, polylines] of polylinesByColor.entries()) {
        const lineSegments = parsePolylines(
          //Merge the the texts's tess using generator function
          (function* (): IterableIterator<PointJson[]> {
            yield* polylines;
          })(),
          blackColor, //color: Using black for text because it contrasts well with our background gradient
        );
        if (lineSegments) {
          try {
            yield* lineSegments;
          } catch (err) {
            this.logError('text', annotationIndex, err);
          }
        } else {
          //logging for follow up in PMI parser
          console.error(
            `Unexpected polyline in PMI JSON with null value, annotationIndex=${annotationIndex}`,
          );
        }
        const now = performance.now();
        if (now - last > maxUIBlockMs) {
          await wait();
          last = now;
        }
      }
    }
  }

  private async *parseText(
    textsByColor: Map<Color, TextTessJson['text'][]>,
    annotationIndex: number,
  ): AsyncIterableIterator<LineSegments<BufferGeometry, LineBasicMaterial>> {
    if (textsByColor.size) {
      let last = performance.now();
      for (const [, texts] of textsByColor.entries()) {
        try {
          const textLineSegments = parsePolylines(
            //Merge the the texts's tess using generator function
            (function* (): IterableIterator<PointJson[]> {
              for (const text of texts) {
                if (text.tess) {
                  yield* text.tess;
                }
              }
            })(),
            blackColor, //color: Using black for text because it contrasts well with our background gradient
          );
          if (textLineSegments) {
            yield* textLineSegments;
          }
        } catch (err) {
          this.logError('text', annotationIndex, err);
        }
        const now = performance.now();
        if (now - last > maxUIBlockMs) {
          await wait();
          last = now;
        }
      }
    }
  }

  private async *parseTriangles(
    trianglesByColor: Map<Color, TrianglesJson['triangles']>,
    annotationIndex: number,
  ): AsyncIterableIterator<Mesh<BufferGeometry, MeshBasicMaterial>> {
    if (trianglesByColor.size) {
      let last = performance.now();
      for (const [color, triangles] of trianglesByColor.entries()) {
        try {
          yield* parseTriangles(triangles, color);
        } catch (err) {
          this.logError('triangles', annotationIndex, err);
        }
        const now = performance.now();
        if (now - last > maxUIBlockMs) {
          await wait();
          last = now;
        }
      }
    }
  }

  private async *parseVertexes(
    vertexesByColor: Map<Color, VertexesJson['vertexes']>,
    annotationIndex: number,
  ): AsyncIterableIterator<Group> {
    if (vertexesByColor.size) {
      let last = performance.now();
      for (const [color, vertexes] of vertexesByColor.entries()) {
        try {
          yield parseVertexes(vertexes, color);
        } catch (err) {
          this.logError('vertexes', annotationIndex, err);
        }
        const now = performance.now();
        if (now - last > maxUIBlockMs) {
          await wait();
          last = now;
        }
      }
    }
  }

  private async *parseCollectedAnnotationData(
    annotationIndex: number,
    {
      cylindersByColor,
      polygonsByColor,
      polylinesByColor,
      textsByColor,
      trianglesByColor,
      vertexesByColor,
    }: CollectedParsedState,
  ) {
    yield* this.parseCylinders(cylindersByColor, annotationIndex);
    cylindersByColor.clear();

    yield* this.parsePolygons(polygonsByColor, annotationIndex);
    polygonsByColor.clear();

    yield* this.parsePolylines(polylinesByColor, annotationIndex);
    polylinesByColor.clear();

    yield* this.parseText(textsByColor, annotationIndex);
    textsByColor.clear();

    yield* this.parseTriangles(trianglesByColor, annotationIndex);
    trianglesByColor.clear();

    yield* this.parseVertexes(vertexesByColor, annotationIndex);
    vertexesByColor.clear();
  }

  private async collectMarkupTessellation(
    annotationIndex: number,
    markupTessIndex: string | number,
    markupTessElem: MarkupTessElemJson,
    collectedParsedState: CollectedParsedState,
  ) {
    let last = performance.now();
    const {
      frames,
      cylindersByColor,
      polygonsByColor,
      polylinesByColor,
      textsByColor,
      trianglesByColor,
    } = collectedParsedState;

    //existingCylinders, existingPolygons, ... are references to values in the `Map`s of cylindersByColor, polygonsByColor, ...
    //They are used to accumulate the cylinders, polygons, ... for a given color.
    //By accumulating these values by color, we can reduce the number of BufferGeometry, which is a performance improvement
    //It is better to have less # (but larger BufferGeometry) than greater # of small BufferGeometry.
    let existingCylinders: CylinderJson['cylinder'][] | undefined;
    let existingPolygons: PolygonJson['polygon'][] | undefined;
    let existingPolylines: PolylineJson['polyline'][] | undefined;
    let existingText: TextTessJson['text'][] | undefined;
    let existingTriangles: TrianglesJson['triangles'] | undefined;

    for (const key in markupTessElem) {
      if (Object.prototype.hasOwnProperty.call(markupTessElem, key)) {
        const value = markupTessElem[
          key as keyof MarkupTessElemJson
        ] as MarkupTessElemJson[keyof MarkupTessElemJson];
        //* See https://docs.techsoft3d.com/exchange/latest/build/group__a3d__tessmarkup.html
        switch (key) {
          //First parse modes:

          //Parse cases of OnOffMarkupModeJson
          case 'faceView':
            if (Array.isArray(value)) {
              //this.logNotImplemented(key, annotationIndex, markupTessIndex);
            }
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate
          case 'frameDraw':
            if (Array.isArray(value)) {
              collectedParsedState.lastFrameDraw = new Vector3(
                ...(value as PointJson),
              );
            } else if (collectedParsedState.lastFrameDraw) {
              //The mode just switched from `frameDraw`, so parse the already collected annotation data for each color in the frame
              //and accumulate the frameGeometry as the items for this frame that we just finished parsing.
              const frameGeometry = [];
              for await (const frameItem of this.parseCollectedAnnotationData(
                annotationIndex,
                collectedParsedState,
              )) {
                frameGeometry.push(frameItem);
              }
              frames.push([
                frameGeometry,
                collectedParsedState.lastFrameDraw.clone(),
              ]);
              collectedParsedState.lastFrameDraw = null;
            }
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate
          case 'fixedSize':
            if (Array.isArray(value)) {
              //this.logNotImplemented(key, annotationIndex, markupTessIndex);
            }
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate
          case 'lineStipple':
            //this.logNotImplemented(key, annotationIndex, markupTessIndex);
            // collectedParsedState.lastLineStipple = Number.isFinite(value) ? value : null;
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate
          case 'lineWidth':
            //this.logNotImplemented(key, annotationIndex, markupTessIndex);
            // collectedParsedState.lastLineWidth = Number.isFinite(value) ? value : null;
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate

          //Parse cases of OverwriteMarkupModeJson
          case 'font':
            //Not logging font is not implemented because we are using text's tess.
            //and I know it is not implemented. Other logging is to help identify cases
            //where we have data and I have not inspected it sufficiently to understand
            //implementation approach.
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate
          case 'color':
            collectedParsedState.lastColor = Number.isFinite(value)
              ? getOrCreateThreeColor(value)
              : null;
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate

          //Parse Geometry cases
          case 'cylinder':
            //accumulate the cylinders in this annotation by color.
            if ((markupTessElem as CylinderJson).cylinder.length) {
              existingCylinders = cylindersByColor.get(
                collectedParsedState.lastColor || blackColor,
              );
              if (!existingCylinders) {
                existingCylinders = [];
                cylindersByColor.set(
                  collectedParsedState.lastColor || blackColor,
                  existingCylinders,
                );
              }
              existingCylinders.push((markupTessElem as CylinderJson).cylinder);
            }
            break;
          case 'polygon':
            //accumulate the polygons in this annotation by color.
            if ((markupTessElem as PolygonJson).polygon.length) {
              //Not using lastColor because sometimes text is black
              //Note: Using blackColor because it contrasts better with PartViewer gradient
              const c = blackColor;
              existingPolygons = polygonsByColor.get(c);
              if (!existingPolygons) {
                existingPolygons = [];
                polygonsByColor.set(c, existingPolygons);
              }
              existingPolygons.push((markupTessElem as PolygonJson).polygon);
            }
            break;
          case 'polyline':
            //accumulate the polylines in this annotation by color.
            if ((markupTessElem as PolylineJson).polyline.length) {
              //Not using lastColor because sometimes text is black
              //Note: Using blackColor because it contrasts better with PartViewer gradient
              const c = blackColor;
              existingPolylines = polylinesByColor.get(c);
              if (!existingPolylines) {
                existingPolylines = [];
                polylinesByColor.set(c, existingPolylines);
              }
              existingPolylines.push((markupTessElem as PolylineJson).polyline);
            }
            break;
          case 'symbol':
            //this.logNotImplemented(key, annotationIndex, markupTessIndex);
            break;
          case 'text':
            //accumulate the vertexes in this annotation by color.
            //Not using lastColor because sometimes text is black
            //Note: Using blackColor because it contrasts better with PartViewer gradient
            existingText = textsByColor.get(blackColor);
            if (!existingText) {
              existingText = [];
              textsByColor.set(blackColor, existingText);
            }
            existingText.push((markupTessElem as TextTessJson).text);
            break;
          case 'triangles':
            //accumulate the triangles in this annotation by color.
            //Note: Sometimes text is rendered as triangles. (TODO This should probably encoded in `text.tess')
            if ((markupTessElem as TrianglesJson).triangles.length) {
              //Not using lastColor because sometimes text is black
              //Note: Using blackColor because it contrasts better with PartViewer gradient
              const c = blackColor;
              existingTriangles = trianglesByColor.get(c);
              if (!existingTriangles) {
                existingTriangles = [];
                trianglesByColor.set(c, existingTriangles);
              }
              //Don't use `existingTriangles.push( ...markupTessElem.triangles)` - It sometimes blows stack because a fn can only have 32768 args
              for (const triangle of (markupTessElem as TrianglesJson)
                .triangles) {
                existingTriangles.push(triangle);
              }
            }
            break;
          case 'vertexes':
            //Not implemented, because when they are vertexes, they appear to be vertexes for polyline and they
            //are ugly...  Need to better understand semantics of what vertexes are used for to decide on its
            //initial visibility and the styling of them.
            //Not logging not implemented because it is noise and we can easily find examples of it.
            // this.logNotImplemented(key, annotationIndex, markupTessIndex);

            // //accumulate the vertexes in this annotation by color.
            // if ((markupTessElem as VertexesJson).vertexes.length) {
            //   existingVertexes = vertexesByColor.get(lastColor || blackColor);
            //   if (!existingVertexes) {
            //     existingVertexes = [];
            //     vertexesByColor.set(lastColor || blackColor, existingVertexes);
            //   }
            //   //When re-implementing see note above about not using `existingTriangles.push( ...markupTessElem.triangles)` because it could blow stack
            //   //because a fn can only have 32768 arguments
            //   existingVertexes.push(...(markupTessElem as VertexesJson).vertexes);
            // }
            break;

          default:
            console.error(
              `Unexpected property key: ${key}, annotationIndex: ${annotationIndex}, markupTessIndex: ${markupTessIndex}`,
            );
            continue; //continue rather than break because there is no markupTessElemGeometry to accumulate
        }
      }
      const now = performance.now();
      if (now - last > maxUIBlockMs) {
        await wait();
        last = now;
      }
    }
  }
}
